import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, combineLatest, debounceTime, takeUntil } from 'rxjs';
import { SharedModule } from '@app/shared/shared.module';
import { RouterModule } from '@angular/router';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { AuthenticationService } from '@app/core/services/AuthenticationService';
import { defaultMessageQuery, MemberMessageDetail, MemberMessageQuery, messagePushStatus, messageTimestamp } from '@app/core/services/MemberMessageService';
import { MessageDisplayedDirective } from './message-displayed.directive';
import * as actions from './member-messages.reducer';

export function parseMessageQuery(params: { get(key: string): string | null }): MemberMessageQuery {
  const size = Number(params.get('pageSize'));
  const page = Number(params.get('page'));
  const type = params.get('type');
  return { page: Number.isSafeInteger(page) && page >= 0 && page <= 100000 ? page : 0,
    pageSize: [10, 25, 50].includes(size) ? size : 25,
    unreadOnly: params.get('unreadOnly') === 'true', type: type === 'Personal' || type === 'General' ? type : 'All',
    search: (params.get('search') || '').slice(0, 200) };
}

@Component({
  selector: 'anms-member-messages', standalone: true,
  imports: [SharedModule, RouterModule, MatButtonToggleModule, MessageDisplayedDirective],
  templateUrl: './member-messages.component.html', styleUrls: ['./member-messages.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MemberMessagesComponent implements OnInit, OnDestroy {
  readonly vm$ = this.store.select(actions.selectMessages);
  readonly skeletonRows = [1, 2, 3, 4, 5];
  readonly timestamp = messageTimestamp;
  readonly pushStatus = messagePushStatus;
  query = { ...defaultMessageQuery };
  adminUserId: string | null = null;
  detailId: string | null = null;
  preferencesOpen = false;
  private identity = '';
  private readonly destroy$ = new Subject<void>();
  private readonly search$ = new Subject<string>();

  constructor(private store: Store, private route: ActivatedRoute, private router: Router,
    private auth: AuthenticationService) {}

  ngOnInit(): void {
    combineLatest([this.route.paramMap, this.route.queryParamMap]).pipe(takeUntil(this.destroy$)).subscribe(() => this.loadRoute());
    this.auth.onUserUpdate().pipe(takeUntil(this.destroy$)).subscribe(() => this.loadRoute());
    this.search$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(search => this.filter({ search }));
    this.vm$.pipe(takeUntil(this.destroy$)).subscribe(vm => {
      // Reading the last unread row or an expired publication can remove the last page.
      if (this.detailId || vm.listLoading || vm.listError || vm.query.page !== this.query.page) return;
      const lastPage = Math.max(0, Math.ceil(vm.page.TotalCount / this.query.pageSize) - 1);
      if (this.query.page > lastPage) this.pageChanged({ pageIndex: lastPage, pageSize: this.query.pageSize });
    });
  }

  private loadRoute(): void {
    if (!this.auth.token) { this.store.dispatch(actions.leave()); return; }
    this.adminUserId = this.route.snapshot.data.adminMessages ? this.route.snapshot.paramMap.get('userId') : null;
    this.detailId = this.route.snapshot.paramMap.get('messageId');
    this.query = parseMessageQuery(this.route.snapshot.queryParamMap);
    const identity = `${this.auth.userData?.User?.Id}:${this.auth.isImpersonating()}:${this.adminUserId || ''}`;
    if (identity !== this.identity) {
      this.identity = identity;
      this.preferencesOpen = false;
      this.store.dispatch(actions.enter({ adminUserId: this.adminUserId, readOnly: !!this.adminUserId || this.auth.isImpersonating() }));
    }
    if (this.detailId) this.store.dispatch(actions.loadDetail({ id: this.detailId }));
    else this.store.dispatch(actions.loadList({ query: this.query }));
  }

  get listRoute(): string[] { return this.adminUserId ? ['/users', this.adminUserId, 'messages'] : ['/messages']; }
  detailRoute(id: string): string[] { return [...this.listRoute, id]; }
  filter(changes: Partial<MemberMessageQuery>): void {
    this.router.navigate(this.listRoute, { queryParams: { ...this.query, ...changes, page: 0 } });
  }
  search(value: string): void { this.query = { ...this.query, search: value }; this.search$.next(value); }
  pageChanged(event: { pageIndex: number; pageSize: number }): void {
    this.router.navigate(this.listRoute, { queryParams: { ...this.query, page: event.pageIndex, pageSize: event.pageSize } });
  }
  retry(): void { this.loadRoute(); }
  displayed(message: MemberMessageDetail): void {
    this.store.dispatch(actions.detailDisplayed({ id: message.Id, version: message.Version }));
  }
  showPreferences(): void {
    this.preferencesOpen = !this.preferencesOpen;
    if (this.preferencesOpen) this.store.dispatch(actions.loadPreferences());
  }
  reloadPreferences(): void { this.store.dispatch(actions.loadPreferences()); }
  savePush(enabled: boolean): void { this.store.dispatch(actions.savePreferences({ enabled })); }
  relatedRoute(message: MemberMessageDetail): string[] | null {
    if (this.adminUserId) return null;
    if (message.RelatedType === 'Invoice' && /^\d+$/.test(message.RelatedId || '')) return ['/invoice-tool', message.RelatedId];
    if (message.RelatedType === 'Document') return ['/mypages/mydocuments'];
    if (message.RelatedType === 'Payout') return ['/transactions'];
    return null;
  }
  ngOnDestroy(): void {
    this.destroy$.next(); this.destroy$.complete();
    this.store.dispatch(actions.leave());
  }
}
